import {
  CreateTableOptions,
  InsertBehaviour,
  RangeReadingOptions,
  RangeWritingOptions,
  ResultColumnIndices,
  ResultWritingOptions,
  WorkbookApi,
} from '@shared/execution/workbook-api';
import { InsertResult, PullResult, PushResult } from '@shared/providers/provider-transport';
import { consumer } from '@shared/utils/stream-utils';
import { pick } from '@shared/utils/utils';
import { PicklistService } from '@v2/services/workbook-api/picklist-service';
import Highland from 'highland';
import { RangeAddress } from 'shared/excel/range-address';
import { TableRangeAddress } from 'shared/excel/table-range-address';
import { NoDataError } from 'shared/utils/flowstep';
import { RangesValuesReader } from './ranges-values-reader';
import { RangesValuesWriter } from './ranges-values-writer';
import { CreateWorksheetOptions } from './workbook-api.types';
import { DataField } from 'shared/providers/types';

export const Limits = {
  [Office.PlatformType.OfficeOnline]: { size: 2 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.8 },
  [Office.PlatformType.PC]: { size: 512 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
  [Office.PlatformType.Mac]: { size: 10 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
  [Office.PlatformType.iOS]: { size: 2 * 1024 * 1024, contentFactor: 2.2, safetyFactor: 0.85 },
};

export class OfficeJsWorkbookApi implements WorkbookApi<Excel.Range> {
  context: Excel.RequestContext;
  constructor(context: Excel.RequestContext) {
    this.context = context;
  }

  private async getSelectedRanges(range: Excel.Range) {
    const selectedRanges = this.context.workbook.getSelectedRanges();
    selectedRanges.areas.load({ address: true });
    selectedRanges.worksheet.load({ id: true, name: true });
    range.load({ address: true, rowCount: true, worksheet: { id: true, name: true } });
    await this.context.sync();

    const rangeWorksheet = range.worksheet;
    const selectedWorksheet = selectedRanges.worksheet;

    if (selectedWorksheet.id !== rangeWorksheet.id) {
      throw new NoDataError(
        `'Selected' mode of range to apply: worksheet of flowstep ('${rangeWorksheet.name}') and active worksheet with selection ('${selectedWorksheet.name}') are not the same`,
      );
    }
    const rangeAddr = RangeAddress.fromString(range.address);

    return selectedRanges.areas.items
      .map((area) => {
        const areaAddr = RangeAddress.fromString(area.address);
        const intersectionAddr = rangeAddr.getIntersection(areaAddr);

        if (!intersectionAddr) {
          return null;
        }

        const selectedRows = RangeAddress.fromCoordinates(
          rangeAddr.startCol,
          intersectionAddr.startRow,
          rangeAddr.endCol,
          intersectionAddr.endRow,
        );
        return range.worksheet.getRange(selectedRows.address);
      })
      .filter((range) => !!range);
  }

  private calcRowCountPerBatch(rowSize: number) {
    const platform = Office.context.platform;
    const limit = Limits[platform] || Limits[Office.PlatformType.OfficeOnline];
    return Math.floor((limit.size / ((rowSize || 1) * limit.contentFactor)) * limit.safetyFactor);
  }

  private async getRangesToRead(range: Excel.Range, options: RangeReadingOptions = {}) {
    if (options.selectedOnly) {
      return await this.getSelectedRanges(range);
    }

    if (options.specifiedRowNumbers?.length) {
      const result: Excel.Range[] = [];

      range.load({ columnIndex: true, columnCount: true, worksheet: { id: true, name: true } });
      await this.context.sync();

      for (const value of options.specifiedRowNumbers) {
        const [startRowIndex, endRowIndex] = value;
        const address = RangeAddress.fromCoordinates(
          range.columnIndex,
          startRowIndex,
          range.columnIndex + range.columnCount - 1,
          endRowIndex,
        );
        result.push(range.worksheet.getRange(address.address));
      }

      return result;
    }

    return [range];
  }

  private async getDataRange(range: Excel.Range) {
    range.load({ rowCount: true });
    await this.context.sync();

    return range.getRow(0).getRowsBelow(range.rowCount - 1);
  }

  async getColumnHeaders(range: Excel.Range) {
    const headerRange = range.getRow(0);
    headerRange.load({ values: true, address: true });
    await this.context.sync();

    return headerRange.values[0] as string[];
  }

  async readRangeData(range: Excel.Range, options?: RangeReadingOptions) {
    const dataRange = await this.getDataRange(range);

    const batchSize = options?.rowSize ? this.calcRowCountPerBatch(options?.rowSize) : 1000;
    const valuesReader = new RangesValuesReader(this.context, batchSize);

    const rangesToRead = await this.getRangesToRead(dataRange, options);
    const rangesToCount = options?.visibleOnly ? rangesToRead.map((r) => r.getVisibleView()) : rangesToRead;
    rangesToCount.forEach((r) => r.load(['rowCount']));
    await this.context.sync();

    return valuesReader.readValues(rangesToRead, options);
  }

  async writeRangeResult<T extends PushResult>(
    range: Excel.Range,
    resultStream: Highland.Stream<T>,
    options: ResultWritingOptions = {},
  ) {
    const { idColumnIndex = -1, idFormat, writeRowStates } = options;
    if (!writeRowStates && idColumnIndex === -1) {
      return null;
    }
    const statusRange = range.getColumnsAfter(1).getColumnsAfter(2);
    statusRange.format.wrapText = false;
    statusRange.format.columnWidth = 100;

    const idColumnRange = idColumnIndex > -1 ? range.getColumn(idColumnIndex) : null;

    const writer = new RangesValuesWriter(this.context);

    if (writeRowStates) {
      const statusHeaderRange = statusRange.getRow(0);
      statusHeaderRange.load({ rowIndex: true });
      statusHeaderRange.format.font.bold = true;
      await statusRange.context.sync();

      await writer.writeValues(statusHeaderRange, [statusHeaderRange.rowIndex, 'Operation results', 'Date time'], {});
    }

    await resultStream
      .consume<T>(
        consumer<T>(async (error, value, push, next) => {
          if (value === Highland.nil) {
            await writer.flushBuffer();
            push(error, value);
            return;
          }

          const insertResult = value as InsertResult;

          if (writeRowStates) {
            await writer.writeValues(statusRange, [
              insertResult.__rowIndex,
              insertResult.status,
              insertResult.timestamp,
            ]);
          }

          if (idColumnRange) {
            await writer.writeValues(idColumnRange, [insertResult.__rowIndex, insertResult.id], {
              rowNumberFormats: idFormat ? [idFormat] : undefined,
            });
          }

          next();
        }),
      )
      .toPromise<Promise<any>>(Promise);

    return statusRange;
  }

  async detectRangeByCell(worksheetId: string, cellAddress: string, options?: { trimToCell?: boolean }) {
    const shiftRangeToTopLeftCell = async (range: Excel.Range) => {
      const topLeftCellAddress = RangeAddress.fromString(cellAddress);
      range.load({columnIndex: true});
      await range.context.sync();
      if (range.columnIndex < topLeftCellAddress.startCol) {
        const colDelta = topLeftCellAddress.startCol - range.columnIndex;
        return range
          .getOffsetRange(0, colDelta)
          .getResizedRange(0, colDelta * -1);
      }
      return range;
    }

    const worksheet = this.context.workbook.worksheets.getItem(worksheetId);
    const cellRange = worksheet.getRange(cellAddress);

    const tables = cellRange.getTables(false);
    tables.load();
    await this.context.sync();
    if (tables.items.length) {
      return shiftRangeToTopLeftCell(tables.items[0].getRange());
    }

    const cellsAround = cellRange.getAbsoluteResizedRange(2, 2);
    const usedRange = cellsAround.getUsedRangeOrNullObject(true);
    await this.context.sync();

    if (usedRange.isNullObject) {
      return null;
    }

    if (!options?.trimToCell) {
      return cellRange.getSurroundingRegion();
    }

    const surrounding = cellRange.getSurroundingRegion();
    surrounding.load(['rowIndex', 'rowCount', 'columnIndex', 'columnCount']);
    cellRange.load(['rowIndex', 'columnIndex']);
    await this.context.sync();

    const columns = surrounding.columnCount - (cellRange.columnIndex - surrounding.columnIndex) - 1;
    const rows = surrounding.rowCount - (cellRange.rowIndex - surrounding.rowIndex) - 1;
    return cellRange.getResizedRange(rows, columns);
  }

  async getRangeBySize(worksheetId: string, topLeftCellAddress: string, rowCount: number, columnCount: number) {
    const worksheet = this.context.workbook.worksheets.getItem(worksheetId);
    return worksheet.getRange(topLeftCellAddress).getAbsoluteResizedRange(rowCount, columnCount);
  }

  async createNewWorksheet(
    worksheetName: string,
    options: CreateWorksheetOptions = {
      activate: false,
    },
  ) {
    const worksheet = this.context.workbook.worksheets.add(worksheetName);
    if (options.activate) {
      worksheet.activate();
    }
    worksheet.load({ id: true });
    await this.context.sync();
    return worksheet.id;
  }

  async writeRangeData<T extends PullResult>(
    range: Excel.Range,
    dataStream: Highland.Stream<T>,
    options: RangeWritingOptions = {},
  ) {
    range.load({ rowIndex: true, rowCount: true, columnCount: true });
    await this.context.sync();

    const { insertRowsMode: insertBehaviour, rowNumberFormats, insertColumnCount } = options;

    let rowIndex = range.rowIndex;
    let insert = insertBehaviour === InsertBehaviour.always;
    let rowCounter = 0;
    const columnCount = range.columnCount + (insertColumnCount ? insertColumnCount : 0);

    let adjustedRowNumberFormats = rowNumberFormats;
    if (rowNumberFormats && columnCount > rowNumberFormats.length) {
      const toFillCount = columnCount - rowNumberFormats.length;
      adjustedRowNumberFormats = [...rowNumberFormats, ...new Array(toFillCount).fill(null)];
    }

    const writer = new RangesValuesWriter(this.context, {
      insertColumnsCount: insertColumnCount,
    });
    return dataStream
      .consume<T>(
        consumer<T>(async (error, values, push, next) => {
          if (values === Highland.nil) {
            await writer.flushBuffer();

            push(null, values);
            return;
          }

          rowCounter = rowIndex - range.rowIndex + 1;

          if (options.insertRowsMode === InsertBehaviour.afterEnd && rowCounter > range.rowCount) {
            insert = true;
          }

          const rowData = values as any[];

          if (columnCount > rowData.length) {
            const toFillCount = columnCount - rowData.length;
            rowData.push(...new Array(toFillCount).fill(null));
          }

          await writer.writeValues(range, [rowIndex, ...rowData], {
            insert,
            rowNumberFormats: adjustedRowNumberFormats,
          });
          rowIndex++;
          next();
        }),
      )
      .toPromise<Promise<any>>(Promise)
      .then(() => rowCounter);
  }

  async appendRangeData(range: Excel.Range, dataStream: Highland.Stream<any[]>, options: RangeWritingOptions = {}) {
    const rangeToAppend = range.getRowsBelow(1);
    return await this.writeRangeData(rangeToAppend, dataStream, options);
  }

  async createTable(range: Excel.Range, options: CreateTableOptions = {}) {
    const { resizeExistent } = options;

    range.load({ rowCount: true });
    const tables = range.getTables(false);
    tables.load();
    await this.context.sync();

    if (tables.items.length === 0) {
      range.worksheet.tables.add(range, true);
      await this.context.sync();
      return;
    }

    if (tables.items.length > 0 && resizeExistent) {
      const table = tables.items[0];
      const tableRange = table.getRange();
      tableRange.load({ rowCount: true });
      await this.context.sync();

      if (range.rowCount > tableRange.rowCount) {
        table.resize(tableRange.getResizedRange(range.rowCount - tableRange.rowCount, 0));
      }
    }
  }

  autofitColumns(range: Excel.Range) {
    range.format.autofitColumns();
  }

  async clearRangeData(range: Excel.Range) {
    const tables = range.getTables(false);
    tables.load();
    await this.context.sync();

    if (tables.items.length > 0) {
      const tableDataRange = tables.items[0].getDataBodyRange();
      tableDataRange.delete(Excel.DeleteShiftDirection.up);
      await this.context.sync();
      return;
    }

    const dataRange = range.getOffsetRange(1, 0).getResizedRange(-1, 0);
    dataRange.delete(Excel.DeleteShiftDirection.up);
  }

  async insertColumnsAfter(range: Excel.Range, columnCount: number): Promise<Excel.Range> {
    const columns = range.getColumnsAfter(columnCount);
    columns.insert('Right');
    await this.context.sync();

    return range.getResizedRange(0, columnCount);
  }

  async insertColumnsAt(range: Excel.Range): Promise<Excel.Range> {
    range.load({ columnCount: true, address: true });
    range.insert('Right');

    await this.context.sync();

    //Range proxy shifts after insertion
    return range.getOffsetRange(0, -range.columnCount);
  }

  addRows(range: Excel.Range, rowCount: number) {
    return range.getResizedRange(rowCount, 0);
  }

  async getRangeAddressInfo(range: Excel.Range) {
    range.load(['address']);
    range.worksheet.load(['id']);
    await this.context.sync();
    return {
      worksheetId: range.worksheet.id,
      address: range.address,
    };
  }

  async getRangeByAddress(addressStr: any) {
    const tableRangeAddress = TableRangeAddress.fromString(addressStr);
    if (tableRangeAddress.isValid) {
      const table = this.context.workbook.tables.getItem(tableRangeAddress.tableName);
      if (!table) {
        return null;
      }
      const worksheet = table.worksheet;
      return worksheet.getRange(addressStr);
    }

    const rangeAddress = RangeAddress.fromString(addressStr);
    if (rangeAddress.isValid && rangeAddress.sheetName) {
      const worksheet = this.context.workbook.worksheets.getItem(rangeAddress.sheetName);
      if (worksheet) {
        return worksheet.getRange(rangeAddress.address);
      }
    }

    return null;
  }

  getRangeColumn(range: Excel.Range, columnIndex: number) {
    if (columnIndex === ResultColumnIndices.OperationResult) {
      return range.getColumnsAfter(3).getColumn(1);
    }
    if (columnIndex === ResultColumnIndices.DateTime) {
      return range.getColumnsAfter(3).getColumn(2);
    }
    return range.getColumn(columnIndex);
  }

  async getRangeValues(range: Excel.Range): Promise<any[][]> {
    range.load({ values: true });
    await this.context.sync();
    return range.values;
  }

  async getRangeCellValue(range: Excel.Range, rowIndex: number, colIndex: number) {
    const cellRange = range.getCell(rowIndex, colIndex);
    cellRange.load({ values: true });
    await this.context.sync();
    return cellRange.values[0][0];
  }

  async writeRangeValue(range: Excel.Range, values: any[][]): Promise<void> {
    range.values = values;
    await this.context.sync();
  }

  async getActiveCellInfo() {
    const activeCell = this.context.workbook.getActiveCell();
    activeCell.load({ address: true, values: true, worksheet: { id: true } });
    await this.context.sync();
    return {
      address: activeCell.address,
      worksheetId: activeCell.worksheet.id,
      value: activeCell.values[0][0],
    };
  }

  async calcRowCountToRead(range: Excel.Range, options?: RangeReadingOptions) {
    if (options?.specifiedRowNumbers?.length) {
      return options.specifiedRowNumbers.reduce(
        (sum, [startRowIndex, endRowIndex]) => sum + (endRowIndex - startRowIndex + 1),
        0,
      );
    }

    const dataRange = !options?.selectedOnly ? await this.getDataRange(range) : range;
    const ranges = options?.selectedOnly ? await this.getSelectedRanges(dataRange) : [dataRange];

    const rowSets = ranges.map((range) => {
      if (options?.visibleOnly) {
        const view = range.getVisibleView();
        view.load({ rowCount: true });
        return view;
      }
      range.load({ rowCount: true });
      return range;
    });
    await this.context.sync();

    return rowSets.reduce((sum, rowSet) => sum + rowSet.rowCount, 0);
  }

  async truncateRangeRows(range: Excel.Range, rowCount: number) {
    range.load({ rowCount: true });
    await this.context.sync();

    if (rowCount >= range.rowCount) {
      return;
    }

    const checkIsEmptyTable = async () => {
      if (range.rowCount > 2) {
        /* Empty table has range of 2 rows (header + empty row) */
        return false;
      }
      const tables = range.getTables();
      tables.load({
        select: ['items/0'],
        expand: ['rows/count'],
      });
      await this.context.sync();
      return tables.items[0]?.rows.count === 0;
    };

    const isEmptyTable = await checkIsEmptyTable();

    if (!isEmptyTable) {
      const rangeToClear = range.getRow(rowCount).getResizedRange(range.rowCount - rowCount - 1, 0);
      rangeToClear.load({ address: true });
      await this.context.sync();
      console.log(`Truncating range ${rangeToClear.address}`);
      rangeToClear.delete('Up');
      await this.context.sync();
    }
  }

  async applyPickLists(tableRange: Excel.Range, flowstepId: string, fields: DataField[]): Promise<void> {
    const picklistService = new PicklistService(this.context);
    await picklistService.removeAllFlowstepLists(flowstepId);

    const listSources = [];

    for (let columnIndex = 0; columnIndex < fields.length; columnIndex++) {
      const field = fields[columnIndex];
      if (!field.options?.length || field.hasMultiselectOptions) {
        listSources.push(null);
        continue;
      }

      const optionNames = field.options.map((option) => option.label);
      const listSize = optionNames.reduce((sum, item) => sum + item.length, 0);

      const listSource =
        listSize < 1000
          ? optionNames.join(',')
          : await picklistService.addFlowstepList(flowstepId, `column${columnIndex}`, optionNames);

      listSources.push(listSource);
    }
    await picklistService.flushBuffer();

    for (let columnIndex = 0; columnIndex < fields.length; columnIndex++) {
      const columnRange = tableRange.getColumn(columnIndex).getOffsetRange(1, 0).getResizedRange(-1, 0);
      columnRange.dataValidation.clear();

      const listSource = listSources[columnIndex];
      if (!listSource) {
        continue;
      }

      const field = fields[columnIndex];
      const { label, objectName, hasRestrictedOptions } = field;

      columnRange.dataValidation.prompt = {
        showPrompt: true,
        title: `${hasRestrictedOptions ? 'Restricted picklist' : 'Picklist'} '${label}'`,
        message: `Object name: '${objectName}'`,
      };
      columnRange.dataValidation.errorAlert = {
        message: `Pickilst '${label}' of object '${objectName}' ${hasRestrictedOptions ? 'is restricted' : `doesn't have such a value`}`,
        showAlert: true,
        title: 'Invalid pickilst value',
        style: hasRestrictedOptions ? Excel.DataValidationAlertStyle.stop : Excel.DataValidationAlertStyle.warning,
      };
      columnRange.dataValidation.rule = {
        list: {
          inCellDropDown: true,
          source: listSource,
        },
      };
    }
    await tableRange.context.sync();
  }

  async clearFilters(worksheetId: string): Promise<any[]> {
    const worksheet = this.context.workbook.worksheets.getItem(worksheetId);

    worksheet.load(['autoFilter']);
    worksheet.tables.load(['items']);

    await this.context.sync();

    const tables = worksheet.tables.items;

    if (tables.length) {
      tables.forEach((x) => x.load(['autoFilter']));
      await this.context.sync();
    }

    const filtersSources = [worksheet, ...tables].map((x) => ({
      autoFilter: x.autoFilter.load(['criteria', 'enabled']),
      range: x.autoFilter.getRangeOrNullObject(),
    }));
    await this.context.sync();

    const activeSources = filtersSources.reduce((out, { autoFilter, range }) => {
      if (!autoFilter.enabled || range.isNullObject) {
        return out;
      }

      const filters = autoFilter.criteria.reduce((activeFilters, x, i) => {
        if (x.criterion1 || x.criterion2 || x.values || x?.dynamicCriteria !== Excel.DynamicFilterCriteria.unknown) {
          activeFilters.push({ columnIndex: i, filter: x });
        }
        return activeFilters;
      }, [] as any[]);

      if (filters.length) {
        out.push({ autoFilter, filters });
        autoFilter.clearCriteria();
      }

      return out;
    }, [] as any[]);

    await this.context.sync();
    return activeSources;
  }

  async applyFilters(_worksheetId: string, sources: any[]): Promise<void> {
    if (sources.length) {
      sources.forEach(({ autoFilter, filters }) =>
        filters.forEach((x: any) => autoFilter.apply(autoFilter.getRange(), x.columnIndex, x.filter)),
      );
      await sources[0].autoFilter.context.sync();
    }
  }

  async disableProtection(
    worksheetId: string,
    protectionPassword?: string,
  ): Promise<Excel.WorksheetProtectionOptions | null> {
    const worksheet = this.context.workbook.worksheets.getItem(worksheetId);
    const { protection } = worksheet;
    protection.load(['options', 'protected', 'isPasswordProtected']);
    await protection.context.sync();

    if (!protection.protected) {
      return null;
    }

    const { options } = protection;
    protection.unprotect(protectionPassword);
    await protection.context.sync();

    return options;
  }

  async enableProtection(worksheetId: string, options: Excel.WorksheetProtectionOptions, protectionPassword?: string) {
    const worksheet = this.context.workbook.worksheets.getItem(worksheetId);
    const protection = worksheet.protection;
    protection.protect(options, protectionPassword);
    await this.context.sync();
  }
}
